import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Tour } from 'antd';
import { VehicleTourContext, VehicleTourContextValue } from './vehicle-tour.context';
import {
  TOUR_STEPS,
  TourStepPlan,
  TourAction,
  TourPhase,
  buildSimulatedLoan,
  computeTourActions,
  selectorFor,
} from './vehicleTourScript';

// Mounted as the element of the `my-vehicle` parent Route (see App.tsx), so
// it wraps BOTH `/admin/my-vehicle` and `/admin/my-vehicle/folio/:folioId`
// via <Outlet/> and survives the navigation between them — necessary
// because the tour's last stop (§5.8) is the folio-detail page, a
// completely separate route/component tree from the tab shell.
//
// This is the "conductor" of §3: it owns which step is showing, derives what
// the app should look like at that step (vehicleTourScript.TOUR_STEPS), and
// diffs that against what it last drove the app to
// (vehicleTourScript.computeTourActions) to decide what to click/navigate
// next. All the safety-relevant decisions (§4) live in the wizard modal
// itself (tourMode prop) — this component only drives buttons that already
// exist, exactly like a fast, automated user would.
const IDLE_PLAN: TourStepPlan = { tab: 'actual', route: 'index', phase: 'pending', wizardScreen: null };

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Deliberately setTimeout, not requestAnimationFrame: rAF callbacks are
// suspended by the browser while the tab isn't the visible/focused one
// (backgrounded tabs, some automation contexts) — that would hang every
// awaiter on this forever, wedging the driver's queue permanently. A short
// timeout still fires either way.
const nextFrame = (): Promise<void> => delay(16);

const clickTarget = (id: string): void => {
  const el = document.querySelector<HTMLElement>(selectorFor(id));
  el?.click();
};

const waitForTarget = async (id: string, timeoutMs = 1500): Promise<HTMLElement | null> => {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const el = document.querySelector<HTMLElement>(selectorFor(id));
    if (el) return el;
    if (Date.now() - start > timeoutMs) return null;
    // eslint-disable-next-line no-await-in-loop
    await delay(32);
  }
};

const VehicleTourProvider: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState<TourPhase>('pending');

  // What we last successfully drove the app to — the baseline the next
  // computeTourActions diff is taken against. null means "unknown", which
  // makes every field of the next plan get (re)applied.
  const currentPlanRef = useRef<TourStepPlan | null>(null);
  const originalTabRef = useRef<'actual' | 'historial'>('actual');
  // Serializes start/goTo/abort so two driver calls never interleave their
  // clicks. On its own this would also make a rapid double-click (or an
  // abort that arrives while a multi-click replay is still mid-flight)
  // queue up BEHIND the earlier call instead of pre-empting it — "stoppable
  // at any point" (§3) means abort can't be left waiting on stale, already-
  // superseded work. generationRef is what lets a newer call cut in line:
  // every goTo/startTour/cleanup claims the next generation synchronously
  // the moment it's invoked (before it ever waits for its queue turn), and
  // performAction/waitForTarget bail out immediately, without clicking
  // anything further, the moment they notice their generation is no longer
  // the current one.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const generationRef = useRef(0);
  const enqueue = useCallback((fn: () => Promise<void>): Promise<void> => {
    const run = queueRef.current.then(fn, fn);
    queueRef.current = run.catch(() => undefined);
    return run;
  }, []);

  const performAction = useCallback(
    async (action: TourAction): Promise<void> => {
      switch (action.type) {
        case 'goToIndexRoute':
          navigate('/admin/my-vehicle');
          await nextFrame();
          break;
        case 'setPhase':
          setPhase(action.phase);
          await nextFrame();
          break;
        case 'setTab':
          clickTarget(action.tab === 'actual' ? 'tour-tab-actual' : 'tour-tab-historial');
          await nextFrame();
          break;
        case 'openWizard':
          clickTarget('tour-action-button');
          await waitForTarget('tour-wizard-lineamientos');
          break;
        case 'wizardNext':
          clickTarget('tour-wizard-next');
          await nextFrame();
          break;
        case 'closeWizard':
          clickTarget('tour-wizard-close');
          await nextFrame();
          break;
        case 'finishWizardScreen':
          clickTarget('tour-wizard-done');
          await nextFrame();
          break;
        case 'openFolioDetail':
          clickTarget('tour-history-row');
          await waitForTarget('tour-folio-detail');
          break;
        default:
          break;
      }
      // A brief pause between actions so a multi-click replay (e.g. going
      // backward across the phase boundary) reads as the app moving, not a
      // flash of intermediate screens.
      await delay(180);
    },
    [navigate]
  );

  // Once a runPlan actually starts, it always runs to completion — there's
  // no mid-action bail-out here. That's deliberate: interrupting it partway
  // would leave currentPlanRef reflecting neither the old nor the new plan,
  // and every later diff (including cleanup's own) is computed against
  // that ref, so a half-applied value there would make future diffs wrong
  // in ways that are hard to reason about. The generation check below (see
  // goTo/cleanup/startTour) only ever skips a queued task that HASN'T
  // started yet — bounding how long an abort can be stuck behind queued
  // work to "however long the one currently-running runPlan takes", a few
  // hundred ms at most, without that correctness risk.
  const runPlan = useCallback(
    async (nextPlan: TourStepPlan): Promise<void> => {
      const actions = computeTourActions(currentPlanRef.current, nextPlan);
      // eslint-disable-next-line no-restricted-syntax
      for (const action of actions) {
        // eslint-disable-next-line no-await-in-loop
        await performAction(action);
      }
      currentPlanRef.current = nextPlan;
    },
    [performAction]
  );

  const goTo = useCallback(
    (targetIndex: number) => {
      // Claimed synchronously, before this even waits its turn in the
      // queue — see the generationRef comment above. A task that hasn't
      // started yet checks this and skips itself entirely once a newer
      // call (typically cleanup, aborting) has superseded it.
      const gen = ++generationRef.current;
      return enqueue(async () => {
        if (generationRef.current !== gen) return;
        if (targetIndex < 0 || targetIndex >= TOUR_STEPS.length) return;
        const step = TOUR_STEPS[targetIndex];
        await runPlan(step.plan);
        await waitForTarget(step.target);
        setStepIndex(targetIndex);
      });
    },
    [enqueue, runPlan]
  );

  const cleanup = useCallback(() => {
    // Bumping generationRef here (synchronously, before this even waits its
    // turn in the queue) is what lets cleanup skip past any not-yet-started
    // goTo calls still queued ahead of it. Its own task below never checks
    // generationRef itself — it always claims the latest generation the
    // moment it's called, so there's never a newer call for it to defer to.
    ++generationRef.current;
    return enqueue(async () => {
      const baseline: TourStepPlan = { ...IDLE_PLAN, tab: originalTabRef.current };
      await runPlan(baseline);
      // Defensive sweep: if a click was missed (e.g. an element hadn't
      // mounted yet), make sure nothing simulated is left open before we
      // flip `active` off and the underlying screens go back to fetching
      // real data.
      if (document.querySelector(selectorFor('tour-wizard-close'))) clickTarget('tour-wizard-close');
      if (document.querySelector(selectorFor('tour-wizard-done'))) clickTarget('tour-wizard-done');
      currentPlanRef.current = null;
      setOpen(false);
      setStepIndex(0);
      setPhase('pending');
    });
  }, [enqueue, runPlan]);

  const startTour = useCallback(
    (currentTab: 'actual' | 'historial') => {
      const gen = ++generationRef.current;
      originalTabRef.current = currentTab;
      currentPlanRef.current = null;
      setPhase('pending');
      setStepIndex(0);
      setOpen(true);
      // Drive to step 0 once the Tour itself has mounted/opened.
      enqueue(async () => {
        if (generationRef.current !== gen) return;
        await nextFrame();
        await runPlan(TOUR_STEPS[0].plan);
        await waitForTarget(TOUR_STEPS[0].target);
      });
    },
    [enqueue, runPlan]
  );

  // §3: "Esc, o clic fuera" — antd's Tour only wires up its own X icon
  // out of the box (verified against @rc-component/tour's source: no
  // keydown listener, and the mask's cutout-frame rects carry no click
  // handler), so both have to be added here explicitly.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cleanup();
    };
    const onMaskClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest('.ant-tour-mask')) cleanup();
    };
    // Capture phase, not bubble: antd's Modal (open under the wizard steps)
    // has its own default `keyboard` Escape handling and stops propagation
    // before it would otherwise reach a bubble-phase window listener —
    // capture runs top-down, so this sees the event first regardless.
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('click', onMaskClick, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('click', onMaskClick, true);
    };
  }, [open, cleanup]);

  const simulatedLoan = useMemo(() => (open ? buildSimulatedLoan(phase) : null), [open, phase]);
  const simulatedHistory = useMemo(() => (simulatedLoan ? [simulatedLoan] : []), [simulatedLoan]);

  const contextValue: VehicleTourContextValue = useMemo(
    () => ({ active: open, simulatedLoan, simulatedHistory, startTour }),
    [open, simulatedLoan, simulatedHistory, startTour]
  );

  const steps = useMemo(
    () =>
      TOUR_STEPS.map((step, index) => ({
        title: step.title,
        description: step.description,
        // antd's TourStepInfo.target type doesn't allow a nullable return,
        // but at runtime it handles a missing target gracefully (the step
        // is simply not shown until waitForTarget/goTo finds it) — this
        // cast just satisfies the type, it doesn't change that behavior.
        target: () => document.querySelector<HTMLElement>(selectorFor(step.target)) as HTMLElement,
        nextButtonProps: {
          children: index === TOUR_STEPS.length - 1 ? 'Finalizar' : 'Siguiente',
          onClick: index === TOUR_STEPS.length - 1 ? () => cleanup() : () => goTo(index + 1),
        },
        prevButtonProps: index === 0 ? undefined : { onClick: () => goTo(index - 1) },
      })),
    [cleanup, goTo]
  );

  return (
    <VehicleTourContext.Provider value={contextValue}>
      <Outlet />
      <Tour
        open={open}
        current={stepIndex}
        steps={steps}
        disabledInteraction
        onClose={() => cleanup()}
        onFinish={() => cleanup()}
      />
    </VehicleTourContext.Provider>
  );
};

export default VehicleTourProvider;
